import { type Theme } from '@/stores/ui.store';

import type { CanvasTokenName, CanvasTokens } from '@littlebird/canvas';

export type CanvasHostTheme = {
  mode: Theme;
  tokens: CanvasTokens;
};

type AliasedToken = 'success' | 'danger' | 'info';

type ResolvedToken = Exclude<CanvasTokenName, AliasedToken>;

type TokenSource = {
  variable: string;
  fallback: string;
  tintOnDark?: true;
};

const TOKEN_SOURCES: Record<ResolvedToken, TokenSource> = {
  background: { variable: '--background', fallback: '#faf9f5' },
  backgroundSubtle: { variable: '--background-subtle', fallback: '#f7f5ed' },
  surface: { variable: '--surface-default', fallback: '#fbfaf5' },
  surfaceHover: { variable: '--surface-hover', fallback: '#f2efe4' },
  surfaceActive: { variable: '--surface-active', fallback: '#edeade' },
  secondary: { variable: '--secondary-default', fallback: '#edeade' },
  secondaryHover: { variable: '--secondary-hover', fallback: '#e8e4d6' },
  secondaryActive: { variable: '--secondary-active', fallback: '#e3dece' },
  borderSubtle: { variable: '--border-subtle', fallback: '#edeade' },
  border: { variable: '--border-default', fallback: '#ece9dc' },
  borderStrong: { variable: '--border-strong', fallback: '#e3dece' },
  borderStrongest: { variable: '--border-strongest', fallback: '#d5cfb8' },
  foreground: { variable: '--foreground-default', fallback: '#1c1c1a' },
  foregroundSubtle: { variable: '--foreground-subtle', fallback: '#5b594f' },
  foregroundMuted: { variable: '--foreground-muted', fallback: '#86847c' },
  accent: { variable: '--accent-default', fallback: '#1b1a14' },
  accentHover: { variable: '--accent-hover', fallback: '#22211a' },
  accentActive: { variable: '--accent-active', fallback: '#2b2a21' },
  accentForeground: { variable: '--accent-foreground', fallback: '#f5f2e9' },
  accentForegroundMuted: {
    variable: '--accent-foreground-muted',
    fallback: '#b8b3a1',
  },
  selection: { variable: '--selection-background', fallback: '#a3d2e6' },
  warning: { variable: '--brand-amber', fallback: '#e0a500' },
  midnightBlue: {
    variable: '--brand-midnight-blue',
    fallback: '#2e1f64',
    tintOnDark: true,
  },
  claret: { variable: '--brand-claret', fallback: '#7f1734', tintOnDark: true },
  darkEmerald: {
    variable: '--brand-dark-emerald-green',
    fallback: '#00674f',
    tintOnDark: true,
  },
  cyan: {
    variable: '--brand-cyan-cornflower-blue',
    fallback: '#008ab3',
    tintOnDark: true,
  },
  emerald: { variable: '--brand-emerald-green', fallback: '#50c878' },
  jordyBlue: { variable: '--brand-jordy-blue', fallback: '#92c2f9' },
  plum: { variable: '--brand-plum', fallback: '#e89eb8' },
  roseQuartz: { variable: '--brand-rose-quartz', fallback: '#a49496' },
  red: { variable: '--brand-red', fallback: '#c41e3a', tintOnDark: true },
  yellow: { variable: '--brand-yellow', fallback: '#ffee8c' },
};

const RESOLVED_TOKEN_NAMES = Object.keys(TOKEN_SOURCES) as ResolvedToken[];

const tintForDarkSurface = (color: string) =>
  `color-mix(in srgb, ${color} 55%, #FFFAEB)`;

const resolveTokenColors = (
  mode: Theme,
  scoped: boolean
): Record<ResolvedToken, string> => {
  const scope = document.createElement('div');
  if (scoped) scope.className = mode;
  scope.style.display = 'none';
  const colorProbe = document.createElement('span');
  scope.appendChild(colorProbe);
  document.body.appendChild(scope);

  const colors = {} as Record<ResolvedToken, string>;
  for (const tokenName of RESOLVED_TOKEN_NAMES) {
    const { variable, fallback, tintOnDark } = TOKEN_SOURCES[tokenName];
    colorProbe.style.color = `var(${variable}, ${fallback})`;
    const resolved = getComputedStyle(colorProbe).color || fallback;
    colors[tokenName] =
      tintOnDark && mode === 'dark' ? tintForDarkSurface(resolved) : resolved;
  }

  scope.remove();
  return colors;
};

const toHostTheme = (
  mode: Theme,
  resolved: Record<ResolvedToken, string>
): CanvasHostTheme => ({
  mode,
  tokens: {
    ...resolved,
    success: resolved.darkEmerald,
    danger: resolved.red,
    info: resolved.cyan,
  },
});

export const readCanvasHostTheme = (mode: Theme): CanvasHostTheme =>
  toHostTheme(mode, resolveTokenColors(mode, false));

export const readCanvasHostThemeForMode = (mode: Theme): CanvasHostTheme =>
  toHostTheme(mode, resolveTokenColors(mode, true));
